/**
 * api/client.ts
 *
 * Thin fetch wrapper for the Mission Control frontend.
 *
 * Every request automatically receives:
 *   X-Tenant-Id:    <currentTenant>          (set by TenantContext on login / switch)
 *   Authorization:  Bearer dev-owner          (dev convenience; replace with real token in production)
 *
 * The `currentTenant` module variable is intentionally kept outside React so it
 * can be read by any module (including non-component utilities) without
 * prop-drilling or context subscription overhead.
 * TenantContext.tsx calls `setTenant()` whenever the active tenant changes, so
 * the two sources of truth stay in sync.
 *
 * Usage:
 *   import { api } from '../api/client'
 *
 *   const runs   = await api.get<RunListResponse>('/v1/runs')
 *   const run    = await api.get<Run>(`/v1/runs/${id}`)
 *   const result = await api.post('/v1/runs', { agentId: '...', input: {} })
 */

// ── Tenant state (module-level — updated by TenantContext) ────────────────────
//
// Default resolution order:
//   1. VITE_DEFAULT_TENANT env var  — set in .env.development to the dev tenant
//      UUID so the Go authz middleware (which requires a UUID, not a slug)
//      accepts every request out-of-the-box when API_TARGET=http://localhost:8080
//   2. Hard-coded string 'dev-tenant' — only used when hitting the mock server
//      (which ignores X-Tenant-Id entirely).
//
// TenantContext.tsx calls setTenant() on mount / on switch, overriding this.

let currentTenant: string =
  (typeof import.meta !== 'undefined' &&
   (import.meta as { env?: Record<string, string> }).env?.VITE_DEFAULT_TENANT) ||
  'dev-tenant'

/** Called by TenantContext when the user switches tenants. */
export function setTenant(tenantId: string): void {
  currentTenant = tenantId
}

/** Read the currently active tenant ID outside of React components. */
export function getTenant(): string {
  return currentTenant
}

// ── Auth token ────────────────────────────────────────────────────────────────
//
// The bearer token is read from localStorage (key `auth:token`) on every request
// so it stays in sync after login / logout without reloading the page. When the
// backend runs in stub auth mode (local dev) and no token is stored, we fall back
// to the `dev-owner` token so the dashboard works out-of-the-box.

const TOKEN_KEY = 'auth:token'

/** Build-time auth mode mirror of the backend AUTH_MODE (see VITE_AUTH_MODE). */
function authMode(): string {
  return (
    (typeof import.meta !== 'undefined' &&
      (import.meta as { env?: Record<string, string> }).env?.VITE_AUTH_MODE) ||
    'stub'
  )
}

/** True when the backend accepts the dev-owner fallback (no real login needed). */
export function isStubAuth(): boolean {
  return authMode() === 'stub'
}

/** True when the build signs in via WorkOS AuthKit (browser PKCE). */
export function isWorkOSAuth(): boolean {
  return authMode() === 'workos'
}

// ── WorkOS recovery hooks (registered by the AuthProvider, workos mode only) ────
//
// These let the async apiFetch recover from an expired WorkOS access token
// without the synchronous stub/jwt path knowing anything about WorkOS. In
// stub/jwt builds they stay null and every branch using them is additionally
// gated behind isWorkOSAuth().

/** Force a token refresh via the SDK, persist it, and return the fresh token. */
let workosRefresher: (() => Promise<string>) | null = null
export function registerWorkosRefresher(fn: () => Promise<string>): void {
  workosRefresher = fn
}

/** Called when a request is still 401 after a forced refresh (→ sign out). */
let authFailureHandler: (() => void) | null = null
export function registerAuthFailureHandler(fn: () => void): void {
  authFailureHandler = fn
}

/** The JWT the user pasted on the login page, if any. */
export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

/** Persist a pasted JWT (called by the login page). */
export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/** Clear the stored JWT (called by logout). */
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/** The bearer token to attach: the stored JWT, or dev-owner in stub mode. */
function bearerToken(): string {
  const stored = getToken()
  if (stored) return stored
  return isStubAuth() ? 'dev-owner' : ''
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: string,
  ) {
    super(`API ${status} ${statusText}`)
    this.name = 'ApiError'
  }
}

/**
 * The user-showable message for a failed request, extracted from the server's
 * error body when that is safe and useful, `fallback` otherwise.
 *
 * Rules, in order:
 *  - Not an ApiError → the error's own message (or `fallback` for non-Errors).
 *  - 5xx → ALWAYS `fallback`, never the body: WriteError's default branch puts
 *    err.Error() on the wire verbatim, and the unguarded junk-id paths still
 *    return raw Postgres text (SQLSTATE and all) — that must never render.
 *  - 4xx → extract from the body, handling every shape the backend emits:
 *    nested {"error":{"code","message"}} (httpx.WriteError), flat
 *    {"error":"string"} (the inbound-email 422), bare text with a trailing
 *    newline (http.Error on the SSE/webhook endpoints), and empty. Anything
 *    unparseable → `fallback`.
 *  - A 4xx body that is MARKUP (opens with '<') → `fallback`. Not every 4xx
 *    comes from the app: nginx answers an oversized upload with a 413 HTML
 *    page, which the >= 500 rule does not cover. A body is only shown when it
 *    is a message; a document never is, at any status.
 *  - A message that is a BARE CODEWORD with no detail ("forbidden",
 *    "conflict") → `fallback`. Derived from the wire format, not a
 *    hand-maintained list: WriteError's message is err.Error(), which for a
 *    bare sentinel equals the humanized code ("not_found" → "not found");
 *    detail exists only when the message carries content beyond that prefix.
 *
 * ApiError.message itself is never returned — statusText is often empty under
 * HTTP/2, so it degrades to "API 404 ".
 */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) {
    return e instanceof Error ? e.message : fallback
  }
  if (e.status >= 500) {
    return fallback
  }

  const raw = e.body.trim()
  if (raw === '') return fallback

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    // Unparseable. This is the ONLY branch that returns raw body text, so the
    // "is this actually a message?" guard belongs here.
    //
    // A body opening with '<' is a DOCUMENT, not a sentence: nginx, Cloudflare
    // and every reverse proxy answer with an HTML error page, and those arrive
    // with a 4xx that the >= 500 rule above does not cover — an nginx 413 on an
    // oversized upload is the reachable case. Rendering it would put
    // "<html>… nginx/1.24.0 (Ubuntu)" in front of a user.
    //
    // Markup detection rather than a length ceiling, deliberately: a bigger
    // error page is still an error page and still opens with '<', so this
    // catches the worst case as surely as the 184-byte one, without inventing a
    // number that would silently swallow a legitimate long message (the 404
    // pinned in the test table is already 176 characters).
    //
    // Bodies opening with '{' or '[' — truncated JSON — are structurally "not a
    // message" too, but no such body is demonstrable here: app 4xx always goes
    // through httpx.WriteError as valid nested JSON. Left out on purpose rather
    // than widened on speculation.
    if (raw.startsWith('<')) return fallback
    // Genuine bare text (http.Error): handler-written strings like "invalid
    // signature" — already user-shaped, and they open with a letter.
    return raw
  }

  if (typeof parsed === 'object' && parsed !== null && 'error' in parsed) {
    const err = (parsed as { error: unknown }).error
    if (typeof err === 'string' && err !== '') {
      return err // flat {"error":"string"}
    }
    if (typeof err === 'object' && err !== null) {
      const { code, message } = err as { code?: unknown; message?: unknown }
      if (typeof message === 'string' && message !== '') {
        // Bare codeword: the message is just the humanized code, no detail.
        if (typeof code === 'string' && message === code.replace(/_/g, ' ')) {
          return fallback
        }
        return message
      }
    }
  }
  return fallback // JSON, but not a shape the backend emits
}

export type FetchOptions = RequestInit & {
  /** Query-string parameters appended to the URL. */
  params?: Record<string, string | number | boolean | null | undefined>
}

/**
 * Core request function. Returns a typed `Response` and throws `ApiError` on
 * 4xx / 5xx responses.
 */
export async function apiFetch(path: string, options: FetchOptions = {}): Promise<Response> {
  const { params, headers: extraHeaders, ...init } = options

  // Append query-string parameters when provided.
  let url = path
  if (params) {
    const qs = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        qs.set(key, String(value))
      }
    }
    const queryString = qs.toString()
    if (queryString) {
      url = `${path}?${queryString}`
    }
  }

  // Issue the request with a specific bearer token. Headers match the historical
  // behaviour exactly; only the token is parameterised so a workos 401 can be
  // retried with a freshly refreshed token (below).
  const send = (token: string): Promise<Response> =>
    fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        // ── Security headers ──────────────────────────────────────────────────
        // X-Tenant-Id scopes every request to the active tenant. The Go backend
        // treats this as the authoritative tenant context alongside the JWT sub.
        // (In workos mode the backend derives the tenant from the token and
        // ignores this header — harmless.)
        'X-Tenant-Id': currentTenant,
        // Bearer token from localStorage (or dev-owner fallback in stub mode).
        Authorization: `Bearer ${token}`,
        // ── Distributed tracing ───────────────────────────────────────────────
        // A fresh UUID v4 per request so browser → Go HTTP → NATS worker logs
        // can all be correlated with a single grep on X-Trace-Id.
        'X-Trace-Id':  crypto.randomUUID(),
        // Merge any caller-supplied headers last so they can override if needed.
        ...(extraHeaders as Record<string, string>),
      },
    })

  // Happy path uses the synchronous token (stub: dev-owner; jwt/workos: the token
  // in localStorage, which the WorkOS SDK keeps fresh via proactive background
  // refresh). stub/jwt behaviour is unchanged.
  let response = await send(bearerToken())

  // workos mode only: a 401 means the cached token was stale (e.g. a background
  // refresh throttled while the tab slept). Force one refresh and retry once; if
  // still 401 the session is dead → hand off to the registered sign-out handler.
  if (response.status === 401 && isWorkOSAuth() && workosRefresher) {
    try {
      const fresh = await workosRefresher()
      response = await send(fresh)
    } catch {
      /* refresh failed — fall through to the 401 surfaced below */
    }
    if (response.status === 401) {
      authFailureHandler?.()
    }
  }

  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new ApiError(response.status, response.statusText, body)
  }

  return response
}

// ── Typed convenience methods ─────────────────────────────────────────────────

export const api = {
  /**
   * GET and deserialise JSON. The generic parameter T is the expected response
   * shape.
   *
   * @example
   * const runs = await api.get<{ runs: Run[] }>('/v1/runs')
   */
  async get<T>(path: string, options?: Omit<FetchOptions, 'method' | 'body'>): Promise<T> {
    const res = await apiFetch(path, { ...options, method: 'GET' })
    return res.json() as Promise<T>
  },

  /**
   * POST a JSON body and deserialise the response.
   *
   * @example
   * const run = await api.post<Run>('/v1/runs', { agentId, input })
   */
  async post<T>(path: string, body?: unknown, options?: Omit<FetchOptions, 'method' | 'body'>): Promise<T> {
    const res = await apiFetch(path, {
      ...options,
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    return res.json() as Promise<T>
  },

  /**
   * PUT a JSON body and deserialise the response.
   */
  async put<T>(path: string, body?: unknown, options?: Omit<FetchOptions, 'method' | 'body'>): Promise<T> {
    const res = await apiFetch(path, {
      ...options,
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    return res.json() as Promise<T>
  },

  /**
   * PATCH a JSON body and deserialise the response.
   */
  async patch<T>(path: string, body?: unknown, options?: Omit<FetchOptions, 'method' | 'body'>): Promise<T> {
    const res = await apiFetch(path, {
      ...options,
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    return res.json() as Promise<T>
  },

  /**
   * DELETE and deserialise the response (may be empty).
   */
  async delete<T = void>(path: string, options?: Omit<FetchOptions, 'method' | 'body'>): Promise<T> {
    const res = await apiFetch(path, { ...options, method: 'DELETE' })
    const text = await res.text()
    return (text ? JSON.parse(text) : undefined) as T
  },

  /**
   * Build a `RequestInit` object pre-populated with the standard auth headers.
   * Useful when passing `init` to components that call `fetch` directly (e.g.
   * ObservabilityPage's `hydrateInit` prop).
   */
  headers(): HeadersInit {
    return {
      'X-Tenant-Id': currentTenant,
      Authorization: `Bearer ${bearerToken()}`,
    }
  },
} as const
